import { apiService } from './api';

// Simple cache implementation without hooks
class SimpleCache {
  private cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  private pendingRequests = new Map<string, Promise<any>>();
  private defaultTTL = 2 * 60 * 1000; // 2 minutes

  private generateKey(endpoint: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${endpoint}:${paramString}`;
  }

  private isExpired(entry: { data: any; timestamp: number; ttl: number }): boolean {
    return Date.now() - entry.timestamp > entry.ttl;
  }

  get<T>(endpoint: string, params?: any): T | null {
    const key = this.generateKey(endpoint, params);
    const entry = this.cache.get(key);
    
    if (!entry || this.isExpired(entry)) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  set<T>(endpoint: string, data: T, params?: any, ttl?: number): void {
    const key = this.generateKey(endpoint, params);
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    });
  }

  has(endpoint: string, params?: any): boolean {
    const key = this.generateKey(endpoint, params);
    const entry = this.cache.get(key);
    return entry ? !this.isExpired(entry) : false;
  }

  invalidate(endpoint?: string): void {
    if (endpoint) {
      const keys = Array.from(this.cache.keys());
      for (const key of keys) {
        if (key.startsWith(`${endpoint}:`)) {
          this.cache.delete(key);
        }
      }
    } else {
      this.cache.clear();
    }
  }

  isPending(endpoint: string, params?: any): boolean {
    const key = this.generateKey(endpoint, params);
    return this.pendingRequests.has(key);
  }

  setPending<T>(endpoint: string, promise: Promise<T>, params?: any): Promise<T> {
    const key = this.generateKey(endpoint, params);
    this.pendingRequests.set(key, promise);
    
    promise.finally(() => {
      this.pendingRequests.delete(key);
    });
    
    return promise;
  }

  getPending<T>(endpoint: string, params?: any): Promise<T> | null {
    const key = this.generateKey(endpoint, params);
    return this.pendingRequests.get(key) || null;
  }
}

// Global cache instance
const cache = new SimpleCache();

class SimpleApiService {
  // Dashboard Stats - Cache for 5 minutes
  async getDashboardStats() {
    const cacheKey = 'dashboard-stats';
    
    // Check cache first
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    // Check if request is already pending
    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    // Make request
    const promise = apiService.getDashboardStats();
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 2 * 60 * 1000); // 2 min (alinhado ao backend)
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Lines/Clients - Cache for 2 minutes, with pagination
  async getLines(options: any = {}) {
    const cacheKey = `lines:${JSON.stringify(options)}`;
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getLines(options);
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 2 * 60 * 1000); // 2 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Lines Stats - Cache for 3 minutes
  async getLinesStats(options: any = {}) {
    const cacheKey = `lines-stats:${JSON.stringify(options)}`;
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getLinesStats(options);
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 3 * 60 * 1000); // 3 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Packages - Cache for 10 minutes (rarely changes)
  async getPackages() {
    const cacheKey = 'packages';
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getPackages();
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 10 * 60 * 1000); // 10 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Nova função para pacotes de linhas (alteração de plano)
  async getLinePackages() {
    return await apiService.getLinePackages();
  }

  // CMS User - Cache for 10 minutes
  async getCmsUserCurrent() {
    const cacheKey = 'cms-user-current';
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getCmsUserCurrent();
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 10 * 60 * 1000); // 10 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }

  // App Downloads - Cache for 3 minutes (apps podem mudar mais frequentemente)
  async getAppDownloads() {
    const cacheKey = 'app-downloads';
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getAppDownloads();
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 3 * 60 * 1000); // 3 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }

  // Invalidate cache methods
  invalidateDashboard() {
    cache.invalidate('dashboard-stats');
  }

  invalidateLines() {
    cache.invalidate('lines');
    cache.invalidate('lines-stats');
  }

  invalidateCmsUser() {
    cache.invalidate('cms-user-current');
  }

  invalidateApps() {
    cache.invalidate('app-downloads');
  }

  // Invalidate all cache
  invalidateAll() {
    cache.invalidate();
  }

  // Methods that don't need caching (mutations)
  async blockLine(lineId: string) {
    const result = await apiService.blockLine(lineId);
    this.invalidateLines();
    return result;
  }

  async unblockLine(lineId: string) {
    const result = await apiService.unblockLine(lineId);
    this.invalidateLines();
    return result;
  }

  async updateLine(lineId: string, data: any) {
    const result = await apiService.updateLine(lineId, data);
    this.invalidateLines();
    return result;
  }

  async renewUser(userId: string, data: any) {
    const result = await apiService.renewUser(userId, data);
    this.invalidateLines();
    return result;
  }

  async changeUserSystem(userId: string, data: any) {
    const result = await apiService.changeUserSystem(userId, data);
    this.invalidateLines();
    return result;
  }

  async changeUserPackage(userId: string, data: any) {
    const result = await apiService.changeUserPackage(userId, data);
    this.invalidateLines();
    return result;
  }

  async extendUser(userId: string, data: any) {
    const result = await apiService.extendUser(userId, data);
    this.invalidateLines();
    return result;
  }


  async getLineUserInfo(lineId: string) {
    return await apiService.getLineUserInfo(lineId);
  }

  async updateCmsUserProfile(data: any) {
    const result = await apiService.updateCmsUserProfile(data);
    this.invalidateCmsUser();
    return result;
  }

  async generateCmsUserApiKey(userId: string) {
    const result = await apiService.generateCmsUserApiKey(userId);
    this.invalidateCmsUser();
    return result;
  }

  // Método para buscar dados públicos do CMS por slug
  async getCmsPublicData(slug: string) {
    const cacheKey = `cms-public-${slug}`;
    
    const cached = cache.get(cacheKey);
    if (cached) {
      return cached;
    }

    const pending = cache.getPending(cacheKey);
    if (pending) {
      return pending;
    }

    const promise = apiService.getCmsPublicData(slug);
    cache.setPending(cacheKey, promise);
    
    try {
      const result = await promise;
      cache.set(cacheKey, result, undefined, 5 * 60 * 1000); // 5 minutes
      return result;
    } catch (error) {
      throw error;
    }
  }
}

export const simpleApiService = new SimpleApiService();
export default simpleApiService;
